import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import authInstance from '../../services/httpClient';

interface SkuResult {
  prefix: string;
  nextSku: string;
  currentHighest: string;
  category: string;
  totalProducts: number;
}

interface Props {
  onBack: () => void;
}

const NextSkuGenerator: React.FC<Props> = ({ onBack }) => {
  const [results, setResults] = useState<SkuResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedSku, setCopiedSku] = useState<string | null>(null);

  const fetchNextSkus = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authInstance.get(
        '/admin/tools/next-sku',
      );

      if (response.data.success) {
        setResults(response.data.data);
      } else {
        setError('Failed to generate SKUs');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to generate SKUs');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCopy = (sku: string) => {
    navigator.clipboard.writeText(sku);
    setCopiedSku(sku);
    setTimeout(() => setCopiedSku(null), 1500);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1000 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
        <IconButton onClick={onBack}>
          <BackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Next SKU Generator
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Scans all catalog products, groups by SKU prefix, and returns the next SKU number
        after the current highest for each prefix. Gaps in numbering are ignored.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          onClick={fetchNextSkus}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} /> : <RefreshIcon />}
        >
          {loading ? 'Scanning catalog...' : 'Generate'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {results.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Prefix</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Next Available SKU</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Current Highest</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Products
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((row) => (
                <TableRow key={row.prefix} hover>
                  <TableCell>
                    <Chip label={row.prefix} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: '0.95rem' }}
                    >
                      {row.nextSku}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {row.currentHighest}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {row.category}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{row.totalProducts}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={copiedSku === row.nextSku ? 'Copied!' : 'Copy SKU'}>
                      <IconButton size="small" onClick={() => handleCopy(row.nextSku)}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default NextSkuGenerator;
